import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, extractApiError } from "../../lib/adminApi";
import {
    AdminPageHeader,
    SectionCard,
} from "../../components/admin/FormPrimitives";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";

export default function AdminBlogList() {
    const [posts, setPosts] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState("");

    const reload = async () => {
        try {
            const { data } = await adminApi.get("/admin/blog-posts");
            setPosts(data || []);
        } catch (err) {
            setError(extractApiError(err));
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        reload();
    }, []);

    const togglePublish = async (post) => {
        try {
            await adminApi.put(`/admin/blog-posts/${post.slug}`, {
                published: !post.published,
            });
            await reload();
        } catch (err) {
            setError(extractApiError(err));
        }
    };

    const onDelete = async (slug) => {
        if (!window.confirm("Supprimer définitivement cet article ?")) return;
        try {
            await adminApi.delete(`/admin/blog-posts/${slug}`);
            await reload();
        } catch (err) {
            setError(extractApiError(err));
        }
    };

    return (
        <div data-testid="admin-blog-list-page">
            <AdminPageHeader
                title="Blog"
                subtitle="Liste des articles. Publier ou dépublier reflète immédiatement le résultat sur le site public."
            />
            <div className="mb-6">
                <Link
                    to="/admin/blog/nouveau"
                    className="btn-gold"
                    data-testid="blog-new-btn"
                >
                    <Plus size={14} /> Nouvel article
                </Link>
            </div>
            {error && (
                <div className="mb-4 text-sm text-red-300">{error}</div>
            )}
            <SectionCard title="Articles" testId="blog-list">
                {!loaded ? (
                    <div className="text-neutral-400">Chargement…</div>
                ) : posts.length === 0 ? (
                    <div className="text-neutral-400">Aucun article.</div>
                ) : (
                    <div className="divide-y divide-ink-border">
                        {posts.map((p) => (
                            <div
                                key={p.slug}
                                className="py-4 flex items-start gap-4 flex-wrap"
                                data-testid={`blog-row-${p.slug}`}
                            >
                                <div className="flex-1 min-w-[200px]">
                                    <div className="font-serif text-lg text-neutral-50">
                                        {p.title?.fr}
                                    </div>
                                    <div className="text-xs text-neutral-500 mt-1">
                                        {p.slug} ·{" "}
                                        {p.published ? (
                                            <span className="text-emerald-400">
                                                publié
                                            </span>
                                        ) : (
                                            <span className="text-neutral-500">
                                                brouillon
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => togglePublish(p)}
                                        className="p-2 border border-ink-border hover:border-gold hover:text-gold text-neutral-300 transition-colors"
                                        title={
                                            p.published
                                                ? "Dépublier"
                                                : "Publier"
                                        }
                                        data-testid={`blog-toggle-${p.slug}`}
                                    >
                                        {p.published ? (
                                            <EyeOff size={16} />
                                        ) : (
                                            <Eye size={16} />
                                        )}
                                    </button>
                                    <Link
                                        to={`/admin/blog/${p.slug}/modifier`}
                                        className="p-2 border border-ink-border hover:border-gold hover:text-gold text-neutral-300 transition-colors inline-flex"
                                        title="Modifier"
                                        data-testid={`blog-edit-${p.slug}`}
                                    >
                                        <Pencil size={16} />
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={() => onDelete(p.slug)}
                                        className="p-2 border border-ink-border hover:border-red-500 hover:text-red-400 text-neutral-300 transition-colors"
                                        title="Supprimer"
                                        data-testid={`blog-delete-${p.slug}`}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </SectionCard>
        </div>
    );
}
